import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';

import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

import Form from '../Common/Form';

const AddPaymentForm = ({ editMode = false }) => {

    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        currency: 'LKR', // Default to LKR
    });

    // --- Handle input changes
    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };


    // Debug
    useEffect(() => {
        console.debug("Form data changed:", formData);
    }, [formData]);


    const displayInfoAlert = (paymentMethod) => {
        let text = ''
        let severity = 'info';
        switch (paymentMethod) {
            case 'CASH':
                text = 'Cash payments will be marked as "Settled" immediately upon submission.';
                severity = 'success';
                break;
            case 'CARD':
                text = 'Card payments will be saved as "Pending". Require manual confirmation for settlement.';
                severity = 'warning';
                break;
            case 'CHEQUE':
                text = 'Cheque payments will be marked as "Pending". Require manual confirmation for settlement.';
                severity = 'warning';
                break;
            case 'BANK_TRANSFER':
                text = 'Bank transfer payments will be saved as "Pending". Require manual confirmation for settlement.';
                severity = 'warning';
                break;
            default:
                text = '';
        }

        if (text !== '')
            return <Alert severity={severity}>{text}</Alert>;
    }

    const getReferenceLabel = (paymentMethod) => {
        switch (paymentMethod) {
            case 'CASH':
                return 'Not required for cash payments';
            case 'CARD':
                return 'Card transaction ID';
            case 'CHEQUE':
                return 'Cheque number';
            case 'BANK_TRANSFER':
                return 'Transaction reference number';
            default:
                return 'Payment reference ID';
        }
    }

    return (
        <Box sx={{ padding: { xs: 2, sm: 3 }, maxWidth: '800px' }}>
            <Form
                resourceName='Payment'
                apiPath='/tenants/payments'
                formData={formData}
                requiredFields={
                    [
                        { name: 'direction', label: 'Direction' },
                        { name: 'method', label: 'Payment method' },
                        { name: 'amount', label: 'Amount' },
                        { name: 'currency', label: 'Currency' },
                    ]
                }
                editMode={editMode}
                onSubmit={() => {
                    navigate(-1);
                }}
                onCancel={() => {
                    navigate(-1);
                }}
            >
                <Stack spacing={3}>
                    {/* Name Group */}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <FormControl fullWidth>
                            <InputLabel id="direction-input-label">Direction *</InputLabel>
                            <Select
                                labelId="direction-input-label"
                                id="direction-select"
                                name="direction"
                                value={formData.direction || ''}
                                label="Direction"
                                onChange={handleChange}
                                required
                            >
                                <MenuItem value="IN">In</MenuItem>
                                <MenuItem value="OUT">Out</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel id="method-input-label">Payment method</InputLabel>
                            <Select
                                labelId="method-input-label"
                                id="method-select"
                                name="method"
                                value={formData.method || ''}
                                label="Payment method"
                                onChange={handleChange}
                                required
                            >
                                <MenuItem value="CASH">Cash</MenuItem>
                                <MenuItem value="CARD">Card</MenuItem>
                                <MenuItem value="CHEQUE">Cheque</MenuItem>
                                <MenuItem value="BANK_TRANSFER">Bank Transfer</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                            label="Amount"
                            name="amount"
                            type='number'
                            placeholder='0.00'

                            // for inputProps with lowercase i is for the native input element
                            inputProps={{
                                min: 0,
                                step: "0.01",
                                onWheel: (e) => e.currentTarget.blur(), // Prevent changing number input on wheel/scroll    
                            }}

                            value={formData.amount || ''}
                            onChange={handleChange}
                            fullWidth
                        />

                        <FormControl fullWidth>
                            <InputLabel id="currency-input-label">Currency</InputLabel>
                            <Select
                                labelId="currency-input-label"
                                id="currency-select"
                                name="currency"
                                value={formData.currency || ''}
                                label="Currency"
                                onChange={handleChange}
                                required
                                disabled // for now only LKR is supported
                            >
                                <MenuItem value='LKR'>LKR</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        {/* Text input for externalReferenceId */}
                        {/* Not required for cash payments */}
                        { formData.method !== 'CASH' &&
                            <TextField
                                label={getReferenceLabel(formData.method)}
                                name="externalReferenceId"
                                value={formData.externalReferenceId || ''}
                                onChange={handleChange}
                                fullWidth
                                disabled={formData.method === 'CASH'} // not required for cash payments
                            />
                        }

                        {/* Date picker for dueAt */}
                        {formData.method === 'CHEQUE' &&
                            <TextField
                                label="Due date"
                                name="dueAt"
                                type="date"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                value={formData.dueAt || ''}
                                onChange={handleChange}
                                fullWidth
                                disabled={formData.method !== 'CHEQUE'} // only for cheque payments
                            />
                        }
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                            label="Notes"
                            name="notes"
                            value={formData.notes || ''}
                            onChange={handleChange}
                            multiline
                            rows={4}
                            inputProps={{ maxLength: 500 }}
                            fullWidth
                        />
                    </Stack>

                    <Stack spacing={2}>
                        {displayInfoAlert(formData.method)}
                    </Stack>
                </Stack>
            </Form>
        </Box>
    );
};

export default AddPaymentForm;